// src/components/ui/List/List.tsx
import React, {useState, useCallback, useRef, useEffect} from 'react';
import {IonIcon, IonSpinner} from '@ionic/react';
import {chevronForwardOutline, closeCircleOutline} from 'ionicons/icons';

// ═══════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════

/** List visual variants */
export type ListVariant =
    | 'default'      // Standard list
    | 'inset'        // Rounded inset list (iOS style)
    | 'card'         // Card-wrapped items
    | 'plain';       // No separators or background

/** List item size presets */
export type ListItemSize = 'sm' | 'md' | 'lg';

/** Line separator style */
export type LineStyle = 'full' | 'inset' | 'none';

/** Avatar/icon shape */
export type AvatarShape = 'circle' | 'rounded' | 'square';

/** Badge/chip configuration */
export interface ListBadge {
    label: string;
    color?: string;
    backgroundColor?: string;
    icon?: string;
}

/** List item start slot configuration */
export interface ListItemStart {
    /** Icon name (ionicon) */
    icon?: string;
    /** Icon color */
    iconColor?: string;
    /** Custom icon element */
    iconElement?: React.ReactNode;
    /** Avatar image URL */
    avatar?: string;
    /** Avatar fallback (initials) */
    avatarFallback?: string;
    /** Avatar shape */
    avatarShape?: AvatarShape;
    /** Custom start element */
    element?: React.ReactNode;
}

/** List item end slot configuration */
export interface ListItemEnd {
    /** Text note */
    note?: string;
    /** Note color class */
    noteColor?: 'success' | 'danger' | 'warning' | 'medium' | 'primary';
    /** Icon name */
    icon?: string;
    /** Icon color */
    iconColor?: string;
    /** Custom end element */
    element?: React.ReactNode;
    /** Show navigation arrow */
    arrow?: boolean;
    /** Badge */
    badge?: ListBadge;
    /** Toggle switch */
    toggle?: {
        checked: boolean;
        onChange: (checked: boolean) => void;
    };
}

/** List item props */
export interface ListItemProps {
    /** Unique key for the item */
    id?: string;
    /** Primary label */
    label: string;
    /** Secondary description */
    description?: string;
    /** Tertiary detail line */
    detail?: string;
    /** Start slot configuration */
    start?: ListItemStart;
    /** End slot configuration */
    end?: ListItemEnd;
    /** Badges/chips displayed inline with label or below */
    badges?: ListBadge[];
    /** Badge position */
    badgePosition?: 'inline' | 'below';
    /** Item size */
    size?: ListItemSize;
    /** Line style for this item */
    lines?: LineStyle;
    /** Whether item is interactive (clickable) */
    interactive?: boolean;
    /** Click handler */
    onClick?: () => void;
    /** Whether item is selected */
    selected?: boolean;
    /** Whether item is disabled */
    disabled?: boolean;
    /** Custom className */
    className?: string;
    /** Custom styles */
    style?: React.CSSProperties;
    /** ARIA label override */
    ariaLabel?: string;
}

/** List group header props */
export interface ListGroupHeaderProps {
    /** Header title */
    title: string;
    /** Header subtitle/note */
    subtitle?: string;
    /** End note (e.g., total amount) */
    endNote?: string;
    /** End note color */
    endNoteColor?: 'success' | 'danger' | 'warning' | 'medium' | 'primary';
    /** Custom end element */
    endElement?: React.ReactNode;
    /** Whether header is sticky */
    sticky?: boolean;
}

/** List group props */
export interface ListGroupProps {
    /** Group key */
    id?: string;
    /** Group header */
    header?: ListGroupHeaderProps;
    /** Items in this group */
    items: ListItemProps[];
    /** Whether group is collapsible */
    collapsible?: boolean;
    /** Whether group is initially expanded */
    defaultExpanded?: boolean;
}

/** Main List props */
export interface ListProps {
    /** List variant */
    variant?: ListVariant;
    /** Default item size */
    size?: ListItemSize;
    /** Default line style */
    lines?: LineStyle;
    /** List items (flat list) */
    items?: ListItemProps[];
    /** Grouped items */
    groups?: ListGroupProps[];
    /** Whether list is loading */
    loading?: boolean;
    /** Loading skeleton count */
    loadingCount?: number;
    /** Empty state message */
    emptyMessage?: string;
    /** Empty state icon */
    emptyIcon?: string;
    /** Empty state action */
    emptyAction?: {
        label: string;
        onClick: () => void;
    };
    /** Custom className */
    className?: string;
    /** Custom styles */
    style?: React.CSSProperties;
    /** ARIA label */
    ariaLabel?: string;
    /** Keyboard navigation */
    keyboardNav?: boolean;
}

// ═══════════════════════════════════════════════════════════════════
// STYLE CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const SIZE_CONFIG: Record<ListItemSize, {
    minHeight: number;
    padding: string;
    labelSize: number;
    descSize: number;
    avatarSize: number;
    iconSize: number;
}> = {
    sm: {
        minHeight: 44,
        padding: '8px 16px',
        labelSize: 14,
        descSize: 12,
        avatarSize: 32,
        iconSize: 20,
    },
    md: {
        minHeight: 56,
        padding: '12px 16px',
        labelSize: 16,
        descSize: 13,
        avatarSize: 40,
        iconSize: 22,
    },
    lg: {
        minHeight: 72,
        padding: '16px 16px',
        labelSize: 17,
        descSize: 14,
        avatarSize: 48,
        iconSize: 24,
    },
};

// ═══════════════════════════════════════════════════════════════════
// AVATAR COMPONENT
// ═══════════════════════════════════════════════════════════════════

interface AvatarProps {
    src?: string;
    fallback?: string;
    shape?: AvatarShape;
    size: number;
}

const Avatar: React.FC<AvatarProps> = ({src, fallback, shape = 'circle', size}) => {
    const [imgError, setImgError] = useState(false);

    const borderRadius = shape === 'circle' ? '50%' : shape === 'rounded' ? 8 : 4;

    if (src && !imgError) {
        return (
            <img
                src={src}
                alt=""
                onError={() => setImgError(true)}
                style={{
                    width: size,
                    height: size,
                    objectFit: 'cover',
                    borderRadius,
                    background: '#fff',
                }}
            />
        );
    }

    return (
        <div style={{
            width: size,
            height: size,
            borderRadius,
            background: 'var(--ion-color-step-100)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--kff-heading-font)',
            fontSize: size * 0.4,
            fontWeight: 600,
            color: 'var(--ion-color-medium)',
        }}>
            {fallback?.charAt(0).toUpperCase() || '?'}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════
// BADGE COMPONENT
// ═══════════════════════════════════════════════════════════════════

const ListBadgeComponent: React.FC<ListBadge> = ({label, color, backgroundColor, icon}) => (
    <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        height: 22,
        padding: '0 8px',
        fontSize: 11,
        fontWeight: 500,
        fontFamily: 'var(--kff-data-font)',
        borderRadius: 11,
        backgroundColor: backgroundColor || 'var(--ion-color-step-100)',
        color: color || 'var(--ion-text-color)',
        whiteSpace: 'nowrap',
    }}>
        {icon && <IonIcon icon={icon} style={{fontSize: 12}} />}
        {label}
    </span>
);

// ═══════════════════════════════════════════════════════════════════
// TOGGLE SWITCH COMPONENT
// ═══════════════════════════════════════════════════════════════════

interface ToggleSwitchProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({checked, onChange, disabled}) => {
    return (
        <button
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            onClick={(e) => {
                e.stopPropagation();
                onChange(!checked);
            }}
            style={{
                width: 48,
                height: 28,
                borderRadius: 14,
                border: 'none',
                padding: 2,
                background: checked ? 'var(--kff-timberline-green)' : 'var(--ion-color-step-200)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s ease',
                opacity: disabled ? 0.5 : 1,
            }}
        >
            <div style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                transform: checked ? 'translateX(20px)' : 'translateX(0)',
                transition: 'transform 0.2s ease',
            }} />
        </button>
    );
};

// ═══════════════════════════════════════════════════════════════════
// LIST ITEM COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const ListItem: React.FC<ListItemProps & {listSize?: ListItemSize; listLines?: LineStyle}> = ({
    label,
    description,
    detail,
    start,
    end,
    badges,
    badgePosition = 'inline',
    size,
    lines,
    interactive = true,
    onClick,
    selected = false,
    disabled = false,
    className,
    style,
    ariaLabel,
    listSize = 'md',
    listLines = 'inset',
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isPressed, setIsPressed] = useState(false);

    const effectiveSize = size || listSize;
    const effectiveLines = lines ?? listLines;
    const sizeConfig = SIZE_CONFIG[effectiveSize];

    const showArrow = end?.arrow || (interactive && onClick);

    const getLineStyle = (): React.CSSProperties => {
        if (effectiveLines === 'none') return {};
        if (effectiveLines === 'full') {
            return {borderBottom: '1px solid var(--ion-border-color)'};
        }
        // inset - border starts after start slot
        return {
            position: 'relative',
        };
    };

    const getBackgroundColor = () => {
        if (disabled) return 'transparent';
        if (isPressed) return 'var(--ion-color-step-100)';
        if (selected) return 'rgba(203, 160, 82, 0.1)';
        if (isHovered || isFocused) return 'var(--ion-color-step-50)';
        return 'transparent';
    };

    return (
        <div
            className={className}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive && !disabled ? 0 : undefined}
            aria-label={ariaLabel || label}
            aria-disabled={disabled}
            aria-selected={selected}
            onClick={interactive && !disabled ? onClick : undefined}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => {
                setIsHovered(false);
                setIsPressed(false);
            }}
            onMouseDown={() => setIsPressed(true)}
            onMouseUp={() => setIsPressed(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.();
                }
            }}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                minHeight: sizeConfig.minHeight,
                padding: sizeConfig.padding,
                background: getBackgroundColor(),
                cursor: interactive && !disabled ? 'pointer' : 'default',
                opacity: disabled ? 0.5 : 1,
                transition: 'background 0.15s ease',
                outline: 'none',
                ...getLineStyle(),
                ...style,
            }}
        >
            {/* Inset border */}
            {effectiveLines === 'inset' && (
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: start ? sizeConfig.avatarSize + 28 : 16,
                    right: 0,
                    height: 1,
                    background: 'var(--ion-border-color)',
                }} />
            )}

            {/* Focus ring */}
            {isFocused && (
                <div style={{
                    position: 'absolute',
                    inset: 2,
                    borderRadius: 8,
                    border: '2px solid var(--kff-forge-gold)',
                    pointerEvents: 'none',
                }} />
            )}

            {/* Start slot */}
            {start && (
                <div style={{flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    {start.element ? (
                        start.element
                    ) : start.avatar || start.avatarFallback ? (
                        <Avatar
                            src={start.avatar}
                            fallback={start.avatarFallback}
                            shape={start.avatarShape}
                            size={sizeConfig.avatarSize}
                        />
                    ) : start.iconElement ? (
                        start.iconElement
                    ) : start.icon ? (
                        <div style={{
                            width: sizeConfig.avatarSize,
                            height: sizeConfig.avatarSize,
                            borderRadius: 8,
                            background: 'var(--ion-color-step-50)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <IonIcon
                                icon={start.icon}
                                style={{
                                    fontSize: sizeConfig.iconSize,
                                    color: start.iconColor || 'var(--ion-color-primary)',
                                }}
                            />
                        </div>
                    ) : null}
                </div>
            )}

            {/* Content */}
            <div style={{flex: 1, minWidth: 0}}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                }}>
                    <span style={{
                        fontFamily: 'var(--kff-heading-font)',
                        fontSize: sizeConfig.labelSize,
                        fontWeight: 500,
                        color: 'var(--ion-text-color)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        {label}
                    </span>
                    {badgePosition === 'inline' && badges?.map((badge, i) => (
                        <ListBadgeComponent key={i} {...badge} />
                    ))}
                </div>
                {description && (
                    <p style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: sizeConfig.descSize,
                        color: 'var(--ion-color-medium)',
                        margin: '2px 0 0',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        {description}
                    </p>
                )}
                {detail && (
                    <p style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: sizeConfig.descSize - 1,
                        color: 'var(--ion-color-step-500)',
                        margin: '2px 0 0',
                    }}>
                        {detail}
                    </p>
                )}
                {badgePosition === 'below' && badges && badges.length > 0 && (
                    <div style={{
                        display: 'flex',
                        gap: 6,
                        flexWrap: 'wrap',
                        marginTop: 6,
                    }}>
                        {badges.map((badge, i) => (
                            <ListBadgeComponent key={i} {...badge} />
                        ))}
                    </div>
                )}
            </div>

            {/* End slot */}
            {(end || showArrow) && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexShrink: 0,
                }}>
                    {end?.element ? (
                        end.element
                    ) : (
                        <>
                            {end?.badge && <ListBadgeComponent {...end.badge} />}
                            {end?.toggle && (
                                <ToggleSwitch
                                    checked={end.toggle.checked}
                                    onChange={end.toggle.onChange}
                                    disabled={disabled}
                                />
                            )}
                            {end?.note && (
                                <span style={{
                                    fontFamily: 'var(--kff-data-font)',
                                    fontSize: sizeConfig.descSize,
                                    fontWeight: 500,
                                    fontFeatureSettings: '"tnum"',
                                    color: end.noteColor
                                        ? `var(--ion-color-${end.noteColor})`
                                        : 'var(--ion-color-medium)',
                                }}>
                                    {end.note}
                                </span>
                            )}
                            {end?.icon && (
                                <IonIcon
                                    icon={end.icon}
                                    style={{
                                        fontSize: sizeConfig.iconSize - 2,
                                        color: end.iconColor || 'var(--ion-color-medium)',
                                    }}
                                />
                            )}
                        </>
                    )}
                    {showArrow && (
                        <IonIcon
                            icon={chevronForwardOutline}
                            style={{
                                fontSize: 18,
                                color: 'var(--ion-color-step-400)',
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════
// LIST GROUP HEADER COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const ListGroupHeader: React.FC<ListGroupHeaderProps> = ({
    title,
    subtitle,
    endNote,
    endNoteColor,
    endElement,
    sticky = false,
}) => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px 8px',
        background: 'var(--ion-color-step-50)',
        position: sticky ? 'sticky' : undefined,
        top: sticky ? 0 : undefined,
        zIndex: sticky ? 5 : undefined,
    }}>
        <div>
            <h3 style={{
                fontFamily: 'var(--kff-heading-font)',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--ion-text-color)',
                margin: 0,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
            }}>
                {title}
            </h3>
            {subtitle && (
                <span style={{
                    fontFamily: 'var(--kff-data-font)',
                    fontSize: 12,
                    color: 'var(--ion-color-medium)',
                }}>
                    {subtitle}
                </span>
            )}
        </div>
        {endElement || (endNote && (
            <span style={{
                fontFamily: 'var(--kff-data-font)',
                fontSize: 13,
                fontWeight: 500,
                fontFeatureSettings: '"tnum"',
                color: endNoteColor
                    ? `var(--ion-color-${endNoteColor})`
                    : 'var(--ion-color-medium)',
            }}>
                {endNote}
            </span>
        ))}
    </div>
);

// ═══════════════════════════════════════════════════════════════════
// LIST SKELETON
// ═══════════════════════════════════════════════════════════════════

const ListSkeleton: React.FC<{count: number; size: ListItemSize}> = ({count, size}) => {
    const sizeConfig = SIZE_CONFIG[size];

    return (
        <>
            {Array.from({length: count}).map((_, i) => (
                <div
                    key={i}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: sizeConfig.padding,
                        minHeight: sizeConfig.minHeight,
                    }}
                >
                    <div style={{
                        width: sizeConfig.avatarSize,
                        height: sizeConfig.avatarSize,
                        borderRadius: 8,
                        background: 'var(--ion-color-step-100)',
                        animation: 'list-pulse 1.5s ease-in-out infinite',
                    }} />
                    <div style={{flex: 1}}>
                        <div style={{
                            height: 14,
                            width: '60%',
                            borderRadius: 4,
                            background: 'var(--ion-color-step-100)',
                            animation: 'list-pulse 1.5s ease-in-out infinite',
                            marginBottom: 6,
                        }} />
                        <div style={{
                            height: 12,
                            width: '40%',
                            borderRadius: 4,
                            background: 'var(--ion-color-step-100)',
                            animation: 'list-pulse 1.5s ease-in-out infinite',
                        }} />
                    </div>
                    <div style={{
                        height: 14,
                        width: 60,
                        borderRadius: 4,
                        background: 'var(--ion-color-step-100)',
                        animation: 'list-pulse 1.5s ease-in-out infinite',
                    }} />
                </div>
            ))}
            <style>{`
                @keyframes list-pulse {
                    0%, 100% { opacity: 0.4; }
                    50% { opacity: 0.7; }
                }
            `}</style>
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════
// LIST EMPTY STATE
// ═══════════════════════════════════════════════════════════════════

interface ListEmptyProps {
    message: string;
    icon?: string;
    action?: {label: string; onClick: () => void};
}

const ListEmpty: React.FC<ListEmptyProps> = ({message, icon, action}) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            textAlign: 'center',
        }}>
            {icon && (
                <IonIcon
                    icon={icon}
                    style={{
                        fontSize: 48,
                        color: 'var(--ion-color-step-300)',
                        marginBottom: 16,
                    }}
                />
            )}
            <p style={{
                fontFamily: 'var(--kff-data-font)',
                fontSize: 14,
                color: 'var(--ion-color-medium)',
                margin: '0 0 16px',
            }}>
                {message}
            </p>
            {action && (
                <button
                    onClick={action.onClick}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    style={{
                        padding: '10px 20px',
                        background: isHovered ? 'var(--kff-forge-gold)' : 'transparent',
                        border: '1px solid var(--kff-forge-gold)',
                        borderRadius: 10,
                        cursor: 'pointer',
                        color: isHovered ? '#0a1a2a' : 'var(--kff-forge-gold)',
                        fontFamily: 'var(--kff-heading-font)',
                        fontSize: 14,
                        fontWeight: 600,
                        transition: 'all 0.2s ease',
                    }}
                >
                    {action.label}
                </button>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN LIST COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const List: React.FC<ListProps> = ({
    variant = 'default',
    size = 'md',
    lines = 'inset',
    items,
    groups,
    loading = false,
    loadingCount = 5,
    emptyMessage = 'No items to display',
    emptyIcon,
    emptyAction,
    className,
    style,
    ariaLabel,
    keyboardNav = true,
}) => {
    const listRef = useRef<HTMLDivElement>(null);
    const [focusedIndex, setFocusedIndex] = useState(-1);

    // Flatten all items for keyboard navigation
    const allItems = items || groups?.flatMap(g => g.items) || [];

    // Keyboard navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (!keyboardNav || allItems.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex(prev => Math.min(prev + 1, allItems.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(prev => Math.max(prev - 1, 0));
                break;
            case 'Home':
                e.preventDefault();
                setFocusedIndex(0);
                break;
            case 'End':
                e.preventDefault();
                setFocusedIndex(allItems.length - 1);
                break;
        }
    }, [keyboardNav, allItems.length]);

    // Get container styles based on variant
    const getContainerStyles = (): React.CSSProperties => {
        const base: React.CSSProperties = {
            background: variant === 'plain' ? 'transparent' : 'var(--ion-background-color)',
        };

        switch (variant) {
            case 'inset':
                return {
                    ...base,
                    margin: '0 16px',
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: 'var(--ion-card-background)',
                };
            case 'card':
                return {
                    ...base,
                    background: 'var(--ion-card-background)',
                    borderRadius: 16,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                };
            default:
                return base;
        }
    };

    const hasItems = (items && items.length > 0) || (groups && groups.some(g => g.items.length > 0));

    return (
        <div
            ref={listRef}
            role="list"
            aria-label={ariaLabel}
            className={className}
            onKeyDown={handleKeyDown}
            style={{
                ...getContainerStyles(),
                ...style,
            }}
        >
            {loading ? (
                <ListSkeleton count={loadingCount} size={size} />
            ) : !hasItems ? (
                <ListEmpty
                    message={emptyMessage}
                    icon={emptyIcon}
                    action={emptyAction}
                />
            ) : groups ? (
                // Grouped list
                groups.map((group, gIndex) => (
                    <div key={group.id || gIndex} role="group">
                        {group.header && <ListGroupHeader {...group.header} />}
                        {group.items.map((item, iIndex) => (
                            <ListItem
                                key={item.id || `${gIndex}-${iIndex}`}
                                {...item}
                                listSize={size}
                                listLines={lines}
                            />
                        ))}
                    </div>
                ))
            ) : (
                // Flat list
                items?.map((item, index) => (
                    <ListItem
                        key={item.id || index}
                        {...item}
                        listSize={size}
                        listLines={lines}
                    />
                ))
            )}
        </div>
    );
};

List.displayName = 'List';
ListItem.displayName = 'ListItem';
ListGroupHeader.displayName = 'ListGroupHeader';

export default List;
